// Swinging a box on the 12 Weeks of Christmas tree when the pointer touches it, like a
// decoration brushed on a real tree. See docs/twelve-weeks-of-christmas.md.
//
// CSS alone cannot do this. An animation started by :hover stops dead the moment the pointer
// leaves, snapping the box straight, so here the swing is started by adding a class and that
// class only comes off when the animation reports it has finished.
//
// DELEGATED from the document, per the house rule in shared/contentLifecycle.js - mouseover
// rather than mouseenter because only mouseover bubbles (a tap on a touch screen fires one too,
// so a tapped box swings). Whether the swing plays at all (no request for reduced motion) is
// left to the stylesheet's media query. Where it says no, the class goes on and nothing moves,
// which is harmless.
//
// Progressive enhancement: without this script the boxes simply do not swing.

import { onContentReady } from './shared/contentLifecycle.js';

const BOX_SELECTOR = '.twelve_weeks_box';

const SWINGING_CLASS = 'swinging';

// A beat after it comes into view before this week's box swings, so the swing is seen rather
// than happening while the page is still painting.
const GREETING_DELAY = 400;

document.addEventListener('mouseover', (event) => {
    const box = event.target.closest(BOX_SELECTOR);

    // mouseover fires again as the pointer crosses the box's own image and number, so a swing
    // already under way is left to finish rather than restarted.
    if (! box || box.classList.contains(SWINGING_CLASS)) {
        return;
    }

    box.classList.add(SWINGING_CLASS);
});

document.addEventListener('animationend', (event) => {
    if (event.animationName === 'twelve_weeks_wobble') {
        event.target.classList.remove(SWINGING_CLASS);
    }
});

// This week's box swings once of its own accord, drawing the eye to it. The same class as a
// touch, so the two cannot fight and animationend above tidies up either. It waits until the
// box is actually in view rather than firing at load: a tree further down the page would
// otherwise swing unseen. On a page where the tree is in view at load, that is the same thing.
//
// Through onContentReady (the house rule for code that finds elements), so a tree arriving in
// swapped content greets too. data-greeted marks a box as handled, which keeps a repeat sweep
// harmless.
onContentReady((root) => {
    root.querySelectorAll(`${BOX_SELECTOR}.current:not([data-greeted])`).forEach((box) => {
        box.dataset.greeted = '';

        const observer = new IntersectionObserver((entries) => {
            if (! entries[0].isIntersecting) {
                return;
            }

            observer.disconnect();
            setTimeout(() => box.classList.add(SWINGING_CLASS), GREETING_DELAY);
        }, { threshold: 0.75 });

        observer.observe(box);
    });
});

// The Copy button beside a week's promo code. It shows the result on the button itself -
// "Copied" with a tick, or "Not copied" with a cross - then settles back to "Copy". The
// labels are all in the markup and the stylesheet shows one per data-copy-state, so this file
// only sets the state. The result is also written into the box's role="status" region, since
// a label changing inside a button is not announced by a screen reader.
//
// Delegated from the document, so the cards need no per-element setup.

const COPY_SELECTOR = '.twelve_weeks_modal_copy';

const COPY_STATUS_SELECTOR = '.twelve_weeks_modal_copy_status';

// How long the result stays on the button before it returns to "Copy".
const COPY_RESULT_DURATION = 3000;

const copyResetTimers = new WeakMap();

// Each state's label inside the button, so the button can be sized to the one showing.
const COPY_LABEL_SELECTORS = {
    idle: '.copy_idle',
    copied: '.copy_copied',
    not_copied: '.copy_not_copied',
};

// The button is only as wide as the label showing, easing between widths as it changes (the
// stylesheet transitions width). Its labels are stacked, which on their own would size it to
// the widest, so it gets an explicit width here: the label's own width plus the button's
// padding and border.
function sizeCopyButton(button, state) {
    const label = button.querySelector(COPY_LABEL_SELECTORS[state]);

    if (! label) {
        return;
    }

    const style = getComputedStyle(button);
    const extra = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
        + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);

    button.style.width = `${label.offsetWidth + extra}px`;
}

// Sized to "Copy" from the start. The closed cards are laid out off-screen rather than hidden,
// so the labels can be measured before any card opens; done again once the fonts have loaded,
// since a label measured in a fallback font is the wrong width.
onContentReady((root) => {
    const size = () => root.querySelectorAll(COPY_SELECTOR).forEach((button) => {
        sizeCopyButton(button, button.dataset.copyState ?? 'idle');
    });

    size();
    document.fonts?.ready.then(size);
});

document.addEventListener('click', (event) => {
    const button = event.target.closest(COPY_SELECTOR);

    if (! button) {
        return;
    }

    copyText(button.dataset.copy).then(
        () => showCopyResult(button, 'copied', 'Code copied'),
        () => showCopyResult(button, 'not_copied', 'Code not copied'),
    );
});

// navigator.clipboard exists only on a secure (HTTPS) page, and writeText can still be refused
// - by the browser's permissions, or with no document focus - so a missing API and a refusal
// both count as "not copied".
function copyText(text) {
    if (! text || ! navigator.clipboard) {
        return Promise.reject();
    }

    return navigator.clipboard.writeText(text);
}

function showCopyResult(button, state, announcement) {
    clearTimeout(copyResetTimers.get(button));

    button.dataset.copyState = state;
    sizeCopyButton(button, state);

    const status = button.parentElement.querySelector(COPY_STATUS_SELECTOR);

    if (status) {
        status.textContent = announcement;
    }

    // A second click restarts the clock rather than being cut short by the first click's timer.
    copyResetTimers.set(button, setTimeout(() => {
        delete button.dataset.copyState;
        sizeCopyButton(button, 'idle');

        if (status) {
            status.textContent = '';
        }
    }, COPY_RESULT_DURATION));
}

// Opening this week's box in its card. Pressing the lid adds .is_open, and the stylesheet does
// the rest - the lid is lifted away and the sheet grows out of the box - so this file owns only the state.
//
// On production an opened box stays open on later visits: remembered in localStorage against
// the week's start date (data-gift), and restored as .already_open, the same end state with no
// animation. The server adds data-gift-remember on production only, so on dev the lid comes off
// every time and the opening can be shown to the client again and again. Nothing leaves the
// browser. Storage can be unavailable (private browsing, blocked site data), so every access is
// guarded - the worst case is a box that has to be opened again.

const GIFT_SELECTOR = '.twelve_weeks_gift';

const LID_SELECTOR = '.twelve_weeks_gift_lid';

const SHEET_SELECTOR = '.twelve_weeks_gift_sheet';

const OPENED_KEY_PREFIX = 'twelve-weeks-opened:';

document.addEventListener('click', (event) => {
    const lid = event.target.closest(LID_SELECTOR);

    if (! lid) {
        return;
    }

    const gift = lid.closest(GIFT_SELECTOR);

    if (! gift || gift.classList.contains('is_open')) {
        return;
    }

    gift.classList.add('is_open');

    if ('giftRemember' in gift.dataset) {
        rememberOpened(gift.dataset.gift);
    }

    // The lid had focus and is about to vanish, which would drop focus to the page. The sheet
    // takes it instead, so a screen reader moves straight to what was inside.
    gift.querySelector(SHEET_SELECTOR)?.focus({ preventScroll: true });
});

// Through onContentReady (the house rule for code that finds elements), so a card arriving in
// swapped content is restored too. Only boxes not already open are looked at, which keeps a
// repeat sweep harmless.
onContentReady((root) => {
    root.querySelectorAll(`${GIFT_SELECTOR}[data-gift-remember]:not(.is_open)`).forEach((gift) => {
        if (wasOpened(gift.dataset.gift)) {
            gift.classList.add('is_open', 'already_open');
        }
    });
});

function rememberOpened(key) {
    try {
        localStorage.setItem(OPENED_KEY_PREFIX + key, '1');
    } catch {
        // Storage unavailable: the box will simply need opening again next visit.
    }
}

function wasOpened(key) {
    try {
        return localStorage.getItem(OPENED_KEY_PREFIX + key) === '1';
    } catch {
        return false;
    }
}

// All the section's text - the intro and the box numbers - is set in Castoro and Inter, which
// load with font-display: swap, so it would be drawn first in a fallback font and jump when the
// real fonts arrived. Instead it starts hidden, and fades in together once this marks the
// section ready. A font that fails to load counts as settled too; the stylesheet also shows
// the text after 3s regardless, for a page with no script at all.
onContentReady((root) => {
    const sections = root.querySelectorAll('section.twelve-weeks-of-christmas:not(.text_ready)');

    if (! sections.length) {
        return;
    }

    const reveal = () => sections.forEach((section) => section.classList.add('text_ready'));

    if (! document.fonts) {
        reveal();
        return;
    }

    Promise.allSettled([
        document.fonts.load('1em Castoro'),
        document.fonts.load('1em Inter'),
    ]).then(reveal);
});
